<template>
  <div class="space-y-20">
    <div class="heading-3">{{ policy.offer.name }}</div>

    <div class="border-static-default-mid rounded-md flex gap-12 border p-12">
      <div class="min-w-[40px]">
        <RevIllustration
          alt="''"
          :height="40"
          :src="orderline.productImage"
          :width="40"
        />
      </div>
      <div class="body-2 flex flex-col gap-4">
        <div class="text-static-default-hi">
          {{ orderline.productTitle }}
        </div>
        <div class="text-static-default-low">
          <div>
            {{
              i18n(translations.purchaseDate, {
                date: i18n.date(
                  new Date(orderline.dateCreation),
                  INSURANCE_DATE_FORMAT,
                ),
              })
            }}
          </div>
          <div v-if="orderline.imei">
            {{
              i18n(translations.imei, {
                imei: orderline.imei,
              })
            }}
          </div>
          <div v-if="orderline.serialNumber">
            {{
              i18n(translations.serialNumber, {
                serialNumber: orderline.serialNumber,
              })
            }}
          </div>
        </div>
      </div>
    </div>

    <div class="body-2 space-y-8">
      <div v-if="policy.startDate" class="flex justify-between">
        <div class="text-static-default-low">
          {{ i18n(translations.coverageSince) }}
        </div>
        <div class="text-static-default-hi">
          {{ i18n.date(new Date(policy.startDate), INSURANCE_DATE_FORMAT) }}
        </div>
      </div>

      <div v-if="policy.endDate" class="flex justify-between">
        <div class="text-static-default-low">
          {{ endDateLabel }}
        </div>
        <div class="text-static-default-hi">
          {{ i18n.date(new Date(policy.endDate), INSURANCE_DATE_FORMAT) }}
        </div>
      </div>

      <div
        v-if="policy.terminationKind && policy.state === 'CANCELLED'"
        class="flex justify-between"
      >
        <div class="text-static-default-low">
          {{ i18n(translations.cancellationReason) }}
        </div>
        <div class="text-static-default-hi">
          {{ cancellationReason }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'

import { INSURANCE_DATE_FORMAT } from '~/scopes/dashboard/services-hub/constant'

import translations from './PolicyAndOrderlineDetails.translations'

const i18n = useI18n()

const props = defineProps<{
  policy: ApiSpecs['/api/insurance/v1/services-hub/policies/:policyId']['GET']['response']['policy']
  orderline: ApiSpecs['/api/insurance/v1/services-hub/policies/:policyId']['GET']['response']['orderline']
}>()

const CANCELLATION_REASONS = {
  POLICY_WITHDRAWAL: i18n(translations.policyWithdrawal),
  POLICY_CANCELLATION: i18n(translations.policyCancellation),
  ORDERLINE_WITHDRAWAL: i18n(translations.orderlineWithdrawal),
  ORDERLINE_CANCELLATION: i18n(translations.orderlineCancellation),
  ORDERLINE_RETURN: i18n(translations.orderlineReturn),
  FAILING_PAYMENT_OCCURRENCE: i18n(translations.failingPaymentOccurrence),
  THEFT: i18n(translations.theft),
  DEATH: i18n(translations.death),
  RISK_DISAPPEARANCE: i18n(translations.riskDisappearance),
  OTHER: i18n(translations.other),
  CHARGEBACK: i18n(translations.chargeback),
  FRAUD: i18n(translations.fraud),
  REPLACEMENT: i18n(translations.replacement),
  SUBSCRIPTION_NOT_ACCEPTED: i18n(translations.subscriptionNotAccepted),
  POLICY_EXPIRED: i18n(translations.policyExpired),
  PAYMENT_FAILED: i18n(translations.paymentFailed),
}

const cancellationReason = computed(() => {
  if (!props.policy.terminationKind) return ''

  return CANCELLATION_REASONS[props.policy.terminationKind]
})

const endDateLabel = computed(() => {
  if (props.policy.state === 'CANCELLED') {
    return i18n(translations.cancelled)
  }

  return i18n(translations.expires)
})
</script>
